import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Link,
  List,
  ListItem,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { isApiError, GUARDIAN_LINK_ERROR_CODES } from "@bw/shared/api";
import { formatDateUtc } from "@bw/shared/utils";
import type {
  ChildGuardianshipResponse,
  GuardianActivityRequestView,
  GuardianControlState,
  GuardianControlView,
  GuardianPermissionView,
  GuardianshipMember,
} from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import {
  GUARDIAN_CHOICE_HELP,
  GUARDIAN_CHOICE_LABEL,
  GUARDIAN_CONTROL_COPY,
} from "../../../common/guardian/controls";
import {
  useChildGuardianship,
  useDecideFamilyRequest,
  useInviteCoGuardian,
  useLeaveGuardianship,
  useSetAccountActive,
  useSetGuardianControl,
} from "../../../common/query/useGuardianLink";

/**
 * ChildDetailPage (/me/family/children/:linkId) — one child a guardian manages
 * (multi-guardian, #2691). `linkId` is the VIEWER's own guardian link; the page
 * never shows or sends a user id. It lists the co-guardians and pending invites,
 * lets any guardian invite another adult, turn account access on or off, and
 * leave (only themselves). A grant that lost a race with a co-guardian's change
 * surfaces a "review the latest" prompt instead of overwriting it.
 */
export function ChildDetailPage() {
  const { linkId = "" } = useParams<{ linkId: string }>();
  const detail = useChildGuardianship(linkId);

  return (
    <Page>
      <Stack spacing={0.5}>
        <Link component={RouterLink} to="/me/family/approvals" variant="body2">
          Family approvals
        </Link>
        <Typography component="h1" sx={{ fontSize: "1.375rem", fontWeight: 700 }}>
          {detail.data?.child_display_name ?? "Your child"}
        </Typography>
      </Stack>
      {detail.isLoading ? (
        <LoadingLine>Loading guardians…</LoadingLine>
      ) : detail.isError || !detail.data ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {isApiError(detail.error) && detail.error.code === GUARDIAN_LINK_ERROR_CODES.NOT_A_GUARDIAN
            ? "You're no longer a guardian for this child."
            : "We couldn't load this child's guardians. Please try again."}
        </Alert>
      ) : (
        <ChildDetail linkId={linkId} detail={detail.data} />
      )}
    </Page>
  );
}

function ChildDetail({ linkId, detail }: { linkId: string; detail: ChildGuardianshipResponse }) {
  const access = detail.permissions.find((item) => item.permission === "account_active");
  return (
    <Stack spacing={4} sx={{ mt: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Chip
          size="small"
          color={detail.child_paused ? "warning" : "success"}
          label={detail.child_paused ? "Paused" : "Active"}
        />
        <Typography variant="body2" color="text.secondary">
          Any guardian can change these settings. Turning access off always wins.
        </Typography>
      </Stack>
      {access ? <AccountAccess linkId={linkId} access={access} childName={detail.child_display_name} /> : null}
      {(detail.controls ?? []).length > 0 ? <Activities linkId={linkId} detail={detail} /> : null}
      <Guardians linkId={linkId} detail={detail} />
      <LeaveGuardianship linkId={linkId} detail={detail} />
    </Stack>
  );
}

function AccountAccess({ linkId, access, childName }: {
  linkId: string;
  access: GuardianPermissionView;
  childName: string;
}) {
  const change = useSetAccountActive(linkId);
  const conflict = isApiError(change.error) && change.error.code === GUARDIAN_LINK_ERROR_CODES.PERMISSION_CHANGED;
  const on = access.state === "granted";
  const who = access.changed_by_you ? "you" : access.changed_by_display_name;

  return (
    <Stack component="section" spacing={1.5} aria-labelledby="access-h">
      <Typography component="h2" variant="h6" id="access-h">
        Account access
      </Typography>
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack spacing={1.5}>
          <FormControlLabel
            sx={{ m: 0, justifyContent: "space-between" }}
            labelPlacement="start"
            control={
              <Switch
                checked={on}
                disabled={change.isPending}
                onChange={(event) => {
                  change.reset();
                  change.mutate({ on: event.target.checked, version: access.version });
                }}
              />
            }
            label={
              <Stack>
                <Typography sx={{ fontWeight: 700 }}>
                  {childName} can use Badminton Warriors
                </Typography>
                {access.changed_at ? (
                  <Typography variant="body2" color="text.secondary">
                    {who
                      ? `${on ? "Turned on" : "Turned off"} by ${who} on ${formatDateUtc(access.changed_at)}`
                      : `${on ? "On" : "Off"} since ${formatDateUtc(access.changed_at)}`}
                  </Typography>
                ) : null}
              </Stack>
            }
          />
          {!on ? (
            <Typography variant="body2" color="text.secondary">
              While access is off, no new lessons can be accepted, booked or paid for. Booked
              sessions stay scheduled. The guardian who arranged the lessons can cancel booked
              sessions from Coaching.
            </Typography>
          ) : null}
          {conflict ? (
            <Alert severity="warning" role="alert">
              Another guardian changed this setting while you were looking. Review the latest
              setting above before turning access back on.
            </Alert>
          ) : change.isError ? (
            <Alert severity="error">{errorMessage(change.error)}</Alert>
          ) : null}
        </Stack>
      </Paper>
    </Stack>
  );
}

/**
 * Activities (#2691): one shared setting per control for all of the child's
 * guardians. A stricter choice always applies; allowing more is checked against
 * the version this guardian saw, so a co-guardian's change in between shows the
 * same "review the latest" prompt as account access.
 */
function Activities({ linkId, detail }: { linkId: string; detail: ChildGuardianshipResponse }) {
  const groups: { key: GuardianControlView["group"]; title: string }[] = [
    { key: "social", title: "Social" },
  ];
  return (
    <Stack component="section" spacing={1.5} aria-labelledby="activities-h">
      <Typography component="h2" variant="h6" id="activities-h" tabIndex={-1} sx={{ outline: "none" }}>
        Activities
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Everything here is allowed unless you change it. Choose Decline to turn something off
        for {detail.child_display_name}, or Ask to approve each new person or place once.
      </Typography>
      {groups.map((group) => {
        const controls = (detail.controls ?? []).filter((control) => control.group === group.key);
        if (controls.length === 0) return null;
        return (
          <Stack key={group.key} spacing={1} role="group" aria-labelledby={`activities-${group.key}`}>
            <Typography id={`activities-${group.key}`} sx={{ fontWeight: 700 }}>
              {group.title}
            </Typography>
            {controls.map((control) => (
              <ControlRow key={control.control} linkId={linkId} control={control} />
            ))}
          </Stack>
        );
      })}
      <ApprovedContacts contacts={detail.approved_contacts ?? []} childName={detail.child_display_name} />
    </Stack>
  );
}

function ControlRow({ linkId, control }: { linkId: string; control: GuardianControlView }) {
  const change = useSetGuardianControl(linkId);
  const conflict = isApiError(change.error) && change.error.code === GUARDIAN_LINK_ERROR_CODES.PERMISSION_CHANGED;
  const copy = GUARDIAN_CONTROL_COPY[control.control];
  const labelId = `control-${control.control}`;
  const who = control.changed_by_you ? "you" : control.changed_by_display_name;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.25}>
        <Stack spacing={0.25}>
          <Typography id={labelId} sx={{ fontWeight: 700 }}>
            {copy.label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {copy.description}
          </Typography>
        </Stack>
        <ToggleButtonGroup
          exclusive
          color="primary"
          size="small"
          fullWidth
          value={control.state}
          // Busy, not disabled: disabling would drop keyboard focus mid-change.
          aria-busy={change.isPending}
          aria-labelledby={labelId}
          onChange={(_event, next: GuardianControlState | null) => {
            if (change.isPending || !next || next === control.state) return;
            change.reset();
            change.mutate({ control: control.control, state: next, version: control.version });
          }}
          sx={{ maxWidth: 420 }}
        >
          {control.choices.map((choice) => (
            <ToggleButton key={choice} value={choice} sx={{ textTransform: "none", fontWeight: 700 }}>
              {GUARDIAN_CHOICE_LABEL[choice]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary">
          {GUARDIAN_CHOICE_HELP[control.state]}
          {control.changed_at && who
            ? ` Set by ${who} on ${formatDateUtc(control.changed_at)}.`
            : control.version === 0
              ? " This is the starting setting."
              : ""}
        </Typography>
        {conflict ? (
          <Alert severity="warning" role="alert">
            Another guardian changed this setting while you were looking. Review the latest
            choice above before allowing more.
          </Alert>
        ) : change.isError ? (
          <Alert severity="error">{errorMessage(change.error)}</Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}

function ApprovedContacts({ contacts, childName }: { contacts: GuardianActivityRequestView[]; childName: string }) {
  // A removed contact's row goes away with the focused button: keep the
  // keyboard user's place on this group's heading.
  const headingRef = useRef<HTMLElement>(null);
  // Removing the last one hides this group, so focus the Activities heading.
  const last = contacts.length <= 1;
  const focusHeading = useCallback(
    () => (last ? document.getElementById("activities-h") : headingRef.current)?.focus(),
    [last],
  );
  // Approvals exist only after a guardian chose Ask: hide the empty group.
  if (contacts.length === 0) return null;
  return (
    <Stack spacing={1} role="group" aria-labelledby="approved-h">
      <Typography id="approved-h" ref={headingRef} tabIndex={-1} sx={{ fontWeight: 700, outline: "none" }}>
        Approved people and places
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {childName} can go ahead with these without asking. Remove one to have them ask again.
      </Typography>
      <List disablePadding aria-label="Approved people and places">
        {contacts.map((contact) => (
          <ListItem key={contact.request_id} disableGutters sx={{ mb: 1 }}>
            <ApprovedContactRow contact={contact} onRemoved={focusHeading} />
          </ListItem>
        ))}
      </List>
    </Stack>
  );
}

function ApprovedContactRow({
  contact,
  onRemoved,
}: {
  contact: GuardianActivityRequestView;
  onRemoved: () => void;
}) {
  const remove = useDecideFamilyRequest();
  const [confirming, setConfirming] = useState(false);
  const removeRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  // Where focus goes when the confirm row opens or closes: into the confirm
  // step, then back to Remove on Cancel (never lost to <body>).
  const moveFocus = useRef<"confirm" | "remove" | null>(null);
  useEffect(() => {
    if (moveFocus.current === "confirm") confirmRef.current?.focus();
    if (moveFocus.current === "remove") removeRef.current?.focus();
    moveFocus.current = null;
  }, [confirming]);
  const who = contact.decided_by_you ? "you" : contact.decided_by_display_name;
  return (
    <Paper variant="outlined" sx={{ width: "100%", p: 2 }}>
      <Stack spacing={1.25}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between" }}
        >
          <Stack sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
              {contact.subject_label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {GUARDIAN_CONTROL_COPY[contact.control].label}
              {contact.decided_at && who ? ` · approved by ${who} on ${formatDateUtc(contact.decided_at)}` : ""}
            </Typography>
          </Stack>
          {confirming ? null : (
            <Button
              ref={removeRef}
              color="error"
              variant="text"
              size="small"
              aria-label={`Remove ${contact.subject_label}`}
              onClick={() => {
                moveFocus.current = "confirm";
                setConfirming(true);
              }}
            >
              Remove
            </Button>
          )}
        </Stack>
        {confirming ? (
          <Stack direction="row" spacing={1} role="group" aria-label={`Confirm removing ${contact.subject_label}`}>
            <Button
              ref={confirmRef}
              color="error"
              variant="contained"
              size="small"
              disabled={remove.isPending}
              onClick={() =>
                remove.mutate({ requestId: contact.request_id, decision: "revoke" }, { onSuccess: onRemoved })
              }
            >
              {remove.isPending ? "Removing…" : "Yes, remove"}
            </Button>
            <Button
              variant="text"
              size="small"
              disabled={remove.isPending}
              onClick={() => {
                moveFocus.current = "remove";
                setConfirming(false);
              }}
            >
              Cancel
            </Button>
          </Stack>
        ) : null}
        {remove.isError ? (
          <Alert severity="error">
            {isApiError(remove.error) && remove.error.status === 409
              ? remove.error.message
              : "We couldn't remove this right now. Please try again."}
          </Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}

function Guardians({ linkId, detail }: { linkId: string; detail: ChildGuardianshipResponse }) {
  return (
    <Stack component="section" spacing={1.5} aria-labelledby="guardians-h">
      <Typography component="h2" variant="h6" id="guardians-h">
        Guardians
      </Typography>
      <List disablePadding aria-label="Guardians">
        {detail.guardians.map((member) => (
          <ListItem key={member.link_id} disableGutters sx={{ mb: 1 }}>
            <GuardianRow member={member} />
          </ListItem>
        ))}
      </List>
      {detail.can_invite ? (
        <InviteGuardian linkId={linkId} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          A child can have up to {detail.max_guardians} guardians, including pending invites.
        </Typography>
      )}
    </Stack>
  );
}

function GuardianRow({ member }: { member: GuardianshipMember }) {
  const pending = member.status === "pending";
  return (
    <Paper variant="outlined" sx={{ width: "100%", p: 2 }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Stack sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700 }} noWrap>
            {pending ? member.email ?? "Invited guardian" : member.display_name}
            {member.is_you ? " (you)" : ""}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {pending
              ? `Invited ${formatDateUtc(member.invited_at)}${member.invited_by_you ? " by you" : ""}`
              : `Guardian since ${formatDateUtc(member.joined_at ?? member.invited_at)}`}
          </Typography>
        </Stack>
        <Chip size="small" variant="outlined" label={pending ? "Invited" : "Active"} />
      </Stack>
    </Paper>
  );
}

function InviteGuardian({ linkId }: { linkId: string }) {
  const invite = useInviteCoGuardian(linkId);
  const [email, setEmail] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = email.trim();
    if (!value) return;
    invite.mutate(value, { onSuccess: () => setEmail("") });
  };
  return (
    <Paper variant="outlined" component="form" onSubmit={submit} sx={{ p: 2.5 }}>
      <Stack spacing={1.5}>
        <Typography sx={{ fontWeight: 700 }}>Invite another guardian</Typography>
        <Typography variant="body2" color="text.secondary">
          They sign in with this email, confirm their details and agree to the guardian terms.
        </Typography>
        <TextField
          label="Their email"
          type="email"
          size="small"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <Button type="submit" variant="contained" size="small" disabled={invite.isPending || !email.trim()}>
          {invite.isPending ? "Sending…" : "Send invite"}
        </Button>
        {invite.isSuccess ? (
          <Alert severity="success" role="status">Invite sent.</Alert>
        ) : invite.isError ? (
          <Alert severity="error">{errorMessage(invite.error)}</Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}

function LeaveGuardianship({ linkId, detail }: { linkId: string; detail: ChildGuardianshipResponse }) {
  const leave = useLeaveGuardianship(linkId);
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const others = detail.guardians.filter((member) => member.status === "active" && !member.is_you).length;
  return (
    <Stack component="section" spacing={1.5} aria-labelledby="leave-h">
      <Typography component="h2" variant="h6" id="leave-h">
        Stop being a guardian
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {others > 0
          ? "Settings you made stay as they are; the other guardians are told and can change them."
          : `You're the only guardian. If you leave, ${detail.child_display_name}'s account is paused until a new guardian approves it.`}
      </Typography>
      {confirming ? (
        <Stack direction="row" spacing={1} role="group" aria-label="Confirm leaving">
          <Button
            color="error"
            variant="contained"
            size="small"
            disabled={leave.isPending}
            onClick={() => leave.mutate(undefined, { onSuccess: () => navigate("/me/family/approvals") })}
          >
            {leave.isPending ? "Leaving…" : "Yes, leave"}
          </Button>
          <Button variant="text" size="small" disabled={leave.isPending} onClick={() => setConfirming(false)}>
            Cancel
          </Button>
        </Stack>
      ) : (
        <Stack direction="row">
          <Button color="error" variant="text" size="small" onClick={() => setConfirming(true)}>
            Leave
          </Button>
        </Stack>
      )}
      {leave.isError ? <Alert severity="error">{errorMessage(leave.error)}</Alert> : null}
    </Stack>
  );
}

function LoadingLine({ children }: { children: ReactNode }) {
  return (
    <Stack direction="row" spacing={1.5} role="status" sx={{ alignItems: "center", mt: 2 }}>
      <CircularProgress size={20} />
      <Typography color="text.secondary">{children}</Typography>
    </Stack>
  );
}

function errorMessage(error: unknown): string {
  const code = isApiError(error) ? error.code : null;
  switch (code) {
    case GUARDIAN_LINK_ERROR_CODES.LIMIT_REACHED:
      return "This child already has the most guardians allowed.";
    case GUARDIAN_LINK_ERROR_CODES.ALREADY_EXISTS:
      return "That email is already a guardian or has a pending invite.";
    case GUARDIAN_LINK_ERROR_CODES.SELF_NOT_ALLOWED:
      return "That email goes to your own inbox. Invite another adult instead.";
    case GUARDIAN_LINK_ERROR_CODES.INVITE_IS_CHILD:
      return "That email goes to your child's own inbox. Invite another adult instead.";
    case GUARDIAN_LINK_ERROR_CODES.INVITE_CHECK_UNAVAILABLE:
      return "We couldn't confirm this email belongs to another adult, so the invite wasn't sent. Try again later, or contact support if it keeps happening.";
    case GUARDIAN_LINK_ERROR_CODES.RATE_LIMITED:
      return "You've sent the most invites allowed for today. Try again tomorrow.";
    case GUARDIAN_LINK_ERROR_CODES.MUST_BE_ADULT:
      return "Guardians must be adults, and a child can't be their own guardian.";
    case GUARDIAN_LINK_ERROR_CODES.DOB_REQUIRED:
      return "Confirm your date of birth on your profile first, then try again.";
    case GUARDIAN_LINK_ERROR_CODES.NOT_A_GUARDIAN:
      return "You're no longer a guardian for this child.";
    default:
      return "We couldn't complete that right now. Please try again.";
  }
}
